import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Command, Search, Plus, Kanban, LayoutGrid, Inbox as InboxIcon, BarChart3, FormInput, FileText,
  Zap, Plug, Users, Sparkles, Moon, Sun, ArrowRight,
} from 'lucide-react';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { useDocsStore } from '@/stores/docsStore';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useFormsStore } from '@/stores/formsStore';
import { useThemeStore } from '@/stores/themeStore';
import { materializeBoard } from '@/lib/ai';
import { remoteGenerateBoard, remoteAskData } from '@/lib/aiRemote';
import { cn } from '@/lib/cn';

type CommandItem =
  | { kind: 'link';   label: string; hint?: string; icon: React.ElementType; to: string; section: string }
  | { kind: 'action'; label: string; hint?: string; icon: React.ElementType; run: () => void; section: string }
  | { kind: 'ai';     label: string; hint?: string; icon: React.ElementType; run: () => void; section: string };

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [aiResult, setAiResult] = useState<string | null>(null);

  const boards = useWorkspaceStore((s) => s.boards);
  const items  = useWorkspaceStore((s) => s.items);
  const docs   = useDocsStore((s) => s.docs);
  const dashboards = useDashboardStore((s) => s.dashboards);
  const forms  = useFormsStore((s) => s.forms);
  const { theme, toggle: toggleTheme } = useThemeStore();

  // Global K / Ctrl+K
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => { if (!open) { setQuery(''); setSelectedIdx(0); setAiResult(null); } }, [open]);

  const commands: CommandItem[] = useMemo(() => {
    const list: CommandItem[] = [];

    // Navigation
    list.push(
      { kind: 'link', label: 'Go to Boards',       section: 'Jump',    icon: Kanban,       to: '/boards' },
      { kind: 'link', label: 'Go to My Work',      section: 'Jump',    icon: LayoutGrid,   to: '/my-work' },
      { kind: 'link', label: 'Go to Inbox',        section: 'Jump',    icon: InboxIcon,    to: '/inbox' },
      { kind: 'link', label: 'Go to Dashboards',   section: 'Jump',    icon: BarChart3,    to: '/dashboards' },
      { kind: 'link', label: 'Go to Forms',        section: 'Jump',    icon: FormInput,    to: '/forms' },
      { kind: 'link', label: 'Go to Docs',         section: 'Jump',    icon: FileText,     to: '/docs' },
      { kind: 'link', label: 'Go to Automations',  section: 'Jump',    icon: Zap,          to: '/automations' },
      { kind: 'link', label: 'Go to Integrations', section: 'Jump',    icon: Plug,         to: '/integrations' },
      { kind: 'link', label: 'Go to People',       section: 'Jump',    icon: Users,        to: '/people' },
      { kind: 'link', label: 'Go to AI Studio',    section: 'Jump',    icon: Sparkles,     to: '/ai' },
    );

    // Boards
    for (const b of boards) {
      list.push({
        kind: 'link',
        section: 'Boards',
        icon: Kanban,
        label: `${b.icon} ${b.name}`,
        to: `/board/${b.id}`,
      });
    }
    // Items
    for (const it of items.slice(0, 30)) {
      list.push({
        kind: 'link',
        section: 'Items',
        icon: ArrowRight,
        label: it.name,
        to: `/board/${it.boardId}`,
      });
    }
    // Docs / Dashboards / Forms
    docs.forEach((d) => list.push({ kind: 'link', section: 'Docs', icon: FileText, label: `${d.icon} ${d.name}`, to: `/docs/${d.id}` }));
    dashboards.forEach((d) => list.push({ kind: 'link', section: 'Dashboards', icon: BarChart3, label: `${d.icon} ${d.name}`, to: `/dashboards/${d.id}` }));
    forms.forEach((f) => list.push({ kind: 'link', section: 'Forms', icon: FormInput, label: `${f.icon} ${f.name}`, to: `/forms/${f.id}` }));

    // Actions
    list.push(
      {
        kind: 'action', section: 'Actions', icon: Plus, label: 'Create new board',
        run: () => navigate('/boards'),
      },
      {
        kind: 'action', section: 'Actions', icon: Plus, label: 'Create new doc',
        run: () => {
          const id = useDocsStore.getState().createDoc('Untitled');
          navigate(`/docs/${id}`);
        },
      },
      {
        kind: 'action', section: 'Actions', icon: Plus, label: 'Create new dashboard',
        run: () => {
          const id = useDashboardStore.getState().createDashboard('Untitled dashboard', '', 'from-neon-blue to-neon-purple');
          navigate(`/dashboards/${id}`);
        },
      },
      {
        kind: 'action', section: 'Actions', icon: theme === 'dark' ? Sun : Moon, label: `Toggle ${theme === 'dark' ? 'light' : 'dark'} mode`,
        run: () => toggleTheme(),
      },
    );

    // AI: treat any non-matching query starting with a natural-language-like phrase as an AI request.
    if (query.trim().length >= 8) {
      const q = query.trim();
      list.push({
        kind: 'ai', section: 'AI', icon: Sparkles, label: `Generate board from "${truncate(q, 40)}"`,
        hint: 'Creates a board with groups + items',
        run: async () => {
          setAiResult('Drafting board…');
          const draft = await remoteGenerateBoard(q);
          const id = materializeBoard(draft);
          setOpen(false);
          navigate(`/board/${id}`);
        },
      });
      list.push({
        kind: 'ai', section: 'AI', icon: Sparkles, label: `Ask data: "${truncate(q, 40)}"`,
        hint: 'Show a quick answer in this palette',
        run: async () => {
          setAiResult('Thinking…');
          const a = await remoteAskData(q);
          setAiResult(a.text);
        },
      });
    }

    return list;
  }, [boards, items, docs, dashboards, forms, query, theme, toggleTheme, navigate]);

  const filtered = useMemo(() => {
    if (!query) return commands.filter((c) => c.section === 'Jump' || c.section === 'Actions').slice(0, 12);
    const q = query.toLowerCase();
    return commands.filter((c) => c.label.toLowerCase().includes(q)).slice(0, 30);
  }, [commands, query]);

  const bySection = useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    for (const c of filtered) {
      const list = map.get(c.section) ?? [];
      list.push(c);
      map.set(c.section, list);
    }
    return map;
  }, [filtered]);

  useEffect(() => { setSelectedIdx(0); }, [query]);

  function run(c: CommandItem) {
    if (c.kind === 'link') { setOpen(false); navigate(c.to); }
    else c.run();
  }

  const flatList = filtered;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
          />
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-x-0 top-[10vh] mx-auto z-[70] max-w-2xl px-4"
          >
            <div className="rounded-2xl border border-border bg-bg-raised shadow-floating overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle">
                <Search className="h-5 w-5 text-fg-muted shrink-0" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(Math.min(selectedIdx + 1, flatList.length - 1)); }
                    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIdx(Math.max(selectedIdx - 1, 0)); }
                    if (e.key === 'Enter')     { e.preventDefault(); const c = flatList[selectedIdx]; if (c) run(c); }
                  }}
                  placeholder="Jump to a page, ask a question, or describe a new board…"
                  className="flex-1 bg-transparent focus:outline-none text-base"
                />
                <kbd className="hidden sm:inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
                  <Command className="h-3 w-3" /> K
                </kbd>
              </div>

              {aiResult && (
                <div className="px-4 py-3 border-b border-border-subtle bg-accent-soft/30 text-sm">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-accent mb-1">
                    <Sparkles className="h-3.5 w-3.5" /> AI
                  </div>
                  <p className="text-pretty">{aiResult}</p>
                </div>
              )}

              <div className="max-h-[50vh] overflow-y-auto py-1">
                {flatList.length === 0 && (
                  <div className="px-6 py-8 text-center text-sm text-fg-muted">
                    No results for "{query}".
                  </div>
                )}
                {Array.from(bySection.entries()).map(([section, list]) => (
                  <div key={section} className="py-1">
                    <div className="px-4 py-1 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
                      {section}
                    </div>
                    {list.map((c) => {
                      const idx = flatList.indexOf(c);
                      const active = idx === selectedIdx;
                      return (
                        <button
                          key={c.label + idx}
                          onMouseEnter={() => setSelectedIdx(idx)}
                          onClick={() => run(c)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2 text-left text-sm',
                            active ? 'bg-accent-soft text-accent' : 'text-fg hover:bg-bg-subtle',
                          )}
                        >
                          <c.icon className={cn('h-4 w-4 shrink-0', active ? 'text-accent' : 'text-fg-muted')} />
                          <span className="flex-1 truncate">{c.label}</span>
                          {'hint' in c && c.hint && <span className="text-xs text-fg-muted">{c.hint}</span>}
                          {c.kind === 'ai' && <span className="badge-accent text-[9px]">AI</span>}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between px-4 py-2 text-[11px] text-fg-muted border-t border-border-subtle">
                <span>↑↓ navigate · ↵ select · esc close</span>
                <span>{flatList.length} result{flatList.length === 1 ? '' : 's'}</span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
